import { Helmet } from "react-helmet-async";

// Base site URL — override via REACT_APP_SITE_URL for staging/production.
export const SITE_URL = process.env.REACT_APP_SITE_URL.replace(/\/$/, "");
export const SITE_NAME = "Upword Media";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * <SEO /> — per-page metadata (title, description, canonical, Open Graph,
 * Twitter card, robots, and one or more JSON-LD blocks).
 */
export const SEO = ({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  noindex = false,
  jsonLd = [],
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Organic Growth Partner for B2B SaaS`;
  const canonical = `${SITE_URL}${path}`;
  const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {blocks.filter(Boolean).map((data, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(data)}</script>
      ))}
    </Helmet>
  );
};

// ---------- Reusable JSON-LD builders ----------
export const FOUNDER = {
  name: "Namrata Kothari",
  jobTitle: "Founder, Upword Media",
  url: SITE_URL,
  sameAs: ["https://www.linkedin.com/company/upword-media/"],
};

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}#organization`,
  name: SITE_NAME,
  alternateName: "Upword",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/upword-logo.png`,
    width: 512,
    height: 512,
  },
  image: `${SITE_URL}/og-image.png`,
  email: "info@getupword.com",
  founder: { "@type": "Person", name: FOUNDER.name, jobTitle: FOUNDER.jobTitle },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Bengaluru",
    addressRegion: "Karnataka",
    addressCountry: "IN",
  },
  contactPoint: [{
    "@type": "ContactPoint",
    email: "info@getupword.com",
    contactType: "sales",
    areaServed: "Worldwide",
    availableLanguage: ["English"],
  }],
  sameAs: ["https://www.linkedin.com/company/upword-media/"],
  knowsAbout: [
    "B2B SaaS content marketing",
    "SEO",
    "AEO (Answer Engine Optimization)",
    "GEO (Generative Engine Optimization)",
    "Content strategy",
    "Content operations",
  ],
  description: "Upword Media is a growth agency for B2B SaaS and tech companies. We build content, social, email, and conversion systems engineered to rank in Google and get cited by ChatGPT, Perplexity, and Claude.",
});

export const personSchema = ({ name = FOUNDER.name, jobTitle = FOUNDER.jobTitle, sameAs = FOUNDER.sameAs } = {}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE_URL}#${name.toLowerCase().replace(/\s+/g, "-")}`,
  name,
  jobTitle,
  worksFor: { "@id": `${SITE_URL}#organization` },
  url: SITE_URL,
  sameAs,
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  publisher: { "@type": "Organization", name: SITE_NAME },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const serviceSchema = ({ name, description, path }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  provider: { "@id": `${SITE_URL}#organization` },
  areaServed: "Worldwide",
  name,
  description,
  url: `${SITE_URL}${path}`,
});

/**
 * HowTo schema — used to mark up the Dandelion Method on /how-we-work so
 * answer engines can extract the 6 steps as a canonical framework attributed
 * to Upword Media.
 */
export const howToSchema = ({ name, description, steps }) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  description,
  totalTime: "P12M",
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    text: s.text,
    url: `${SITE_URL}/how-we-work#step-${i + 1}`,
  })),
});

/**
 * BlogPosting JSON-LD for individual blog posts.
 * Pass: { title, description, slug, image, author, datePublished, dateModified }
 * `datePublished`/`dateModified` should be ISO 8601 strings.
 */
export const blogPostingSchema = ({
  title,
  description,
  slug,
  image = DEFAULT_OG_IMAGE,
  author = { name: "Upword Media", url: SITE_URL },
  datePublished,
  dateModified,
}) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: title,
  description,
  image: [image],
  datePublished,
  dateModified: dateModified || datePublished,
  author: { "@type": "Person", ...author },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/upword-logo.png` },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE_URL}/blog/${slug}`,
  },
  url: `${SITE_URL}/blog/${slug}`,
});

/** Blog index page schema — Blog + BreadcrumbList sit alongside via jsonLd array. */
export const blogSchema = ({ description, posts = [] }) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  name: `${SITE_NAME} Blog`,
  url: `${SITE_URL}/blog`,
  description,
  publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  blogPost: posts.map((p) => ({
    "@type": "BlogPosting",
    headline: p.title,
    url: `${SITE_URL}/blog/${p.slug}`,
    datePublished: p.datePublished,
  })),
});
